import { useCallback, useState } from 'react'
import type { JSX } from 'react'

import { useMutation } from '@apollo/client/react'

import { QUERY_ACCOUNT as APP_SHELL_QUERY_ACCOUNT } from '@buffer-mono/app-shell/src/common/graphql/account'
import {
  Button,
  ChevronDownIcon,
  DropdownMenu,
  Flex,
  Heading,
  Link,
  Loader,
  Text,
} from '@buffer-mono/popcorn'

import { GetAccount, useAccount } from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'
import { logError } from '~publish/legacy/utils/logError'
import TwoFactor from '~publish/pages/Settings/Profile/components/TwoFactor'

import styles from './Setup2FARequiredPage.module.css'

const SET_CURRENT_ORGANIZATION = graphql(/* GraphQL */ `
  mutation Setup2FARequiredSetCurrentOrganization($organizationId: String) {
    accountSetCurrentOrganization(organizationId: $organizationId)
  }
`)

/**
 * Reached when the current organization's server-derived
 * `shouldEnforce2FASetup` is true — the actor must finish 2FA setup
 * before they can use the org. The page offers two actions:
 *   1. "Set up 2FA" — opens the existing `TwoFactor` setup dialog
 *      (controlled mode). The user can cancel/close the dialog and come
 *      back to this page; only `onSetupComplete` after the recovery code
 *      step actually clears the gate.
 *   2. "Switch to <Org>" — for users belonging to multiple orgs, lets
 *      them jump to an org that doesn't enforce 2FA. Hidden when no such
 *      org exists. Triggers the `accountSetCurrentOrganization` mutation
 *      and a hard navigation so all consumers (Apollo caches, session,
 *      Redux) re-initialise.
 *
 * If the gate stops applying (e.g. an admin disabled enforcement) we
 * intentionally don't auto-redirect — there's no infinite-loop risk left
 * to guard against because the page no longer races with app-shell's
 * `Enforce2FARedirect` via SPA navigation. Instead we render an explicit
 * "you're all set" CTA so the user can leave manually.
 */
export function Setup2FARequiredPage(): JSX.Element {
  const { account, loading, refetchAccount } = useAccount()
  const [setCurrentOrganization, { loading: switchingOrg }] = useMutation(
    SET_CURRENT_ORGANIZATION,
  )

  const [setupOpen, setSetupOpen] = useState(false)
  const [justCompletedSetup, setJustCompletedSetup] = useState(false)

  const gateApplies = Boolean(
    account?.currentOrganization?.shouldEnforce2FASetup,
  )

  // Other orgs the user can switch into where the gate doesn't apply for
  // them. `shouldEnforce2FASetup` already accounts for the per-org toggle,
  // the rollout split, and the actor's own 2FA — so any org returning
  // false is a valid escape hatch.
  const switchableOrgs = (account?.organizations ?? []).filter(
    (org) =>
      org?.id &&
      org.id !== account?.currentOrganization?.id &&
      !org.shouldEnforce2FASetup,
  )

  const handleSetupComplete = useCallback((): void => {
    // Flip into the post-setup success state so the user sees a clear
    // confirmation rather than the generic "not required" copy, then
    // refetch the account so the gate clears, and finally hard-navigate
    // back to the publish app root. We can't `await` the refetch directly
    // here (TwoFactor's `onSetupComplete` is sync `() => void`), so we
    // chain the navigation after the refetch promise resolves — the
    // success state covers the brief in-flight window.
    setJustCompletedSetup(true)
    const navigate = (): void => {
      window.location.replace('/')
    }
    const refetchPromise = refetchAccount?.()
    if (refetchPromise) {
      void refetchPromise.then(navigate, navigate)
    } else {
      navigate()
    }
  }, [refetchAccount])

  const handleGoHome = useCallback((): void => {
    window.location.replace('/')
  }, [])

  const handleSwitchOrganization = useCallback(
    async (organizationId: string): Promise<void> => {
      try {
        // Refetch both account queries before navigating so the new
        // currentOrganization is hydrated before the next page's gate
        // evaluation runs — a bare reload occasionally caught a stale
        // cache and re-triggered the 2FA gate against the previous org.
        await setCurrentOrganization({
          variables: { organizationId },
          refetchQueries: [
            { query: GetAccount },
            { query: APP_SHELL_QUERY_ACCOUNT },
          ],
          awaitRefetchQueries: true,
        })
        // Hard navigation so Redux, app-shell event listeners, and the
        // server-side session all re-initialise from scratch.
        window.location.assign('/')
      } catch (error) {
        logError(error, { metaData: { organizationId } })
      }
    },
    [setCurrentOrganization],
  )

  if (loading) {
    return (
      <div className={styles.page}>
        <Loader />
      </div>
    )
  }

  if (!gateApplies) {
    // Two ways into this branch:
    //  - User just finished setup → `justCompletedSetup` is true and a
    //    hard-navigation is already queued; this view is the brief
    //    success flash before the redirect kicks in.
    //  - User landed here directly without enforcement applying → render
    //    a manual escape hatch (button) since there's no auto-redirect.
    const title = justCompletedSetup
      ? 'Two-Factor Authentication Is Set Up'
      : 'You’re All Set'
    const description = justCompletedSetup
      ? 'Taking you back to Buffer…'
      : 'Two-factor authentication isn’t required for you right now.'
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <Flex direction="column" gap="md" align="start">
            <Flex direction="column" gap="2xs">
              <Heading as="h1" size="medium">
                {title}
              </Heading>
              <Text color="subtle">{description}</Text>
            </Flex>
            {justCompletedSetup ? (
              <Loader />
            ) : (
              <Button variant="primary" size="large" onClick={handleGoHome}>
                Go to Buffer
              </Button>
            )}
          </Flex>
        </div>
      </div>
    )
  }

  const orgName = account?.currentOrganization?.name

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <Flex direction="column" gap="2xs">
          <Heading as="h1" size="large">
            Two-Factor Authentication Required
          </Heading>
          <Text color="subtle">
            {orgName ? (
              <>
                Your organization, <strong>{orgName}</strong>, requires
                two-factor authentication before you can use Buffer.
              </>
            ) : (
              'Your organization requires two-factor authentication before you can use Buffer.'
            )}{' '}
            You&apos;ll only need to set it up once.{' '}
            <Link
              external
              href="https://support.buffer.com/articles/using-two-factor-authentication-in-buffer-FClTl67lZB?utm_source=buffer&utm_medium=learn-more-link&utm_campaign=learn-more"
            >
              Learn more
            </Link>
          </Text>
        </Flex>
        <Flex gap="sm" align="center" wrap="wrap">
          <Button
            variant="primary"
            size="large"
            onClick={(): void => setSetupOpen(true)}
          >
            Set Up Two-Factor Authentication
          </Button>
          {switchableOrgs.length > 0 && (
            <DropdownMenu>
              <DropdownMenu.Trigger>
                <Button
                  variant="tertiary"
                  size="large"
                  loading={switchingOrg}
                  disabled={switchingOrg}
                >
                  Switch Organization
                  <ChevronDownIcon />
                </Button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Content align="start">
                <DropdownMenu.Label>Switch to</DropdownMenu.Label>
                {switchableOrgs.map((org) => (
                  <DropdownMenu.Item
                    key={org.id}
                    onSelect={(): void => {
                      void handleSwitchOrganization(org.id)
                    }}
                  >
                    {org.name}
                  </DropdownMenu.Item>
                ))}
              </DropdownMenu.Content>
            </DropdownMenu>
          )}
        </Flex>
      </div>

      <TwoFactor
        forced
        open={setupOpen}
        onOpenChange={setSetupOpen}
        onSetupComplete={handleSetupComplete}
      />
    </div>
  )
}
